import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { usePager } from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Ban, FileText, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { fmtIST } from "../lib/time";

/**
 * Unified Approvals hub (Feb-2026).
 *
 * Single page that merges the previously-separate /edit-approvals and
 * /over-receipt-approvals screens. Sub-tabs let PH/SA cycle through:
 *   - Edit Requests (record edits awaiting field-level approval)
 *   - Over-Receipt Requests (>105% schedule variance lines)
 *   - Deleted Records (transaction soft-deletes awaiting approval)
 *
 * Old routes /edit-approvals and /over-receipt-approvals redirect here.
 */
export default function Approvals() {
  const { user } = useAuth();
  const isAdminish = !!user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";
  const [section, setSection] = useState("edit");

  if (!isAdminish) {
    return (
      <div data-testid="approvals-page">
        <PageHeader title="Approvals" subtitle="Plant Head / Super Admin only"/>
        <div className="p-8 text-[#C62828] text-sm">Access denied.</div>
      </div>
    );
  }

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" subtitle="Edit requests · Over-receipts · Deletes — one inbox"
        helpText="All pending decisions for Plant Head & Super Admin live here. Switch between Edit, Over-Receipt and Delete approvals with the tabs below. Approving an edit/over-receipt applies the change immediately; rejection requires a note."/>
      <div className="p-8 space-y-4">
        <div className="flex gap-1 border-b border-[#EEEEEE]" data-testid="approval-sub-tabs">
          <SectionTab id="edit" cur={section} onClick={setSection} icon={Pencil} label="Edit Requests"/>
          <SectionTab id="over" cur={section} onClick={setSection} icon={AlertTriangle} label="Over-Receipt"/>
          <SectionTab id="delete" cur={section} onClick={setSection} icon={Trash2} label="Delete Requests"/>
        </div>
        {section === "edit" && <EditSection/>}
        {section === "over" && <OverSection/>}
        {section === "delete" && <DeleteSection/>}
      </div>
    </div>
  );
}

function SectionTab({ id, cur, onClick, icon: Icon, label }) {
  const active = cur === id;
  return (
    <button onClick={() => onClick(id)} data-testid={`approval-sub-${id}`}
      className={`flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest font-bold border-b-2 -mb-px ${
        active ? "text-[#1565C0] border-[#1565C0]" : "text-[#0D2B5E]/60 border-transparent hover:text-[#1565C0]"
      }`}>
      <Icon size={13}/> {label}
    </button>
  );
}

/* ============================ EDIT requests ============================ */
function EditSection() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    const r = await api.get(`/edit-requests${tab === "pending" ? "?status=pending" : ""}`);
    setRows((r.data || []).filter((x) => tab === "pending" ? x.status === "pending" : x.status === tab));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const { visibleRows, controls } = usePager(rows, 10);

  const decide = async (rid, action) => {
    if (action === "reject" && !(note[rid] || "").trim()) {
      toast.error("Rejection note required.");
      return;
    }
    setBusy((b) => ({ ...b, [rid]: true }));
    try {
      await api.post(`/edit-requests/${rid}/${action}`, { note: note[rid] || "" });
      toast.success(action === "approve" ? "Approved + applied" : "Rejected");
      setNote((n) => ({ ...n, [rid]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [rid]: false }));
    }
  };

  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm">
      <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center gap-3 bg-[#F5F5F5]">
        <FileText size={14}/>
        <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Edit Requests</div>
        <div className="ml-auto flex gap-2">
          {["pending", "approved", "rejected"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`edit-tab-${t}`}
              className={`text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm ${tab === t ? "bg-[#1565C0] text-white" : "border border-[#EEEEEE] text-[#0D2B5E]/70"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
            <tr>
              <th className="text-left px-4 py-2">Module</th>
              <th className="text-left px-4 py-2">Record</th>
              <th className="text-left px-4 py-2">By</th>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Changes</th>
              <th className="text-right px-4 py-2">Decision</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.id} className="border-b border-[#EEEEEE]" data-testid={`edit-req-${r.id}`}>
                <td className="px-4 py-2 text-xs uppercase tracking-widest font-bold text-[#0D2B5E]/70">{r.module}</td>
                <td className="px-4 py-2 text-xs font-mono">{r.record_label || r.record_id?.slice(0, 8)}</td>
                <td className="px-4 py-2 text-xs">{r.requested_by_name || r.requested_by || "—"}</td>
                <td className="px-4 py-2 text-xs font-mono">{fmtIST(r.created_at || r.requested_at)}</td>
                <td className="px-4 py-2 text-xs">
                  {Object.entries(r.changes || {}).map(([k, v]) => (
                    <div key={k}><span className="font-bold">{k}:</span> <span className="text-[#C62828] line-through">{fmt(v?.from)}</span> → <span className="text-[#2E7D32]">{fmt(v?.to)}</span></div>
                  ))}
                  {(!r.changes || Object.keys(r.changes).length === 0) && <span className="text-[#0D2B5E]/40">—</span>}
                  {r.reason && <div className="mt-1 italic text-[#0D2B5E]/60">"{r.reason}"</div>}
                </td>
                <td className="px-4 py-2 text-right">
                  {r.status === "pending" && (
                    <div className="flex flex-col gap-1 items-end">
                      <input value={note[r.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                        placeholder="Note (required for reject)" data-testid={`edit-note-${r.id}`}
                        className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm w-48"/>
                      <div className="flex gap-1">
                        <button onClick={() => decide(r.id, "approve")} disabled={busy[r.id]} data-testid={`edit-approve-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold bg-[#2E7D32] text-white px-2 py-1 rounded-sm disabled:opacity-50 flex items-center gap-1"><Check size={10}/>Approve</button>
                        <button onClick={() => decide(r.id, "reject")} disabled={busy[r.id]} data-testid={`edit-reject-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold bg-[#C62828] text-white px-2 py-1 rounded-sm disabled:opacity-50 flex items-center gap-1"><Ban size={10}/>Reject</button>
                      </div>
                    </div>
                  )}
                  {r.status !== "pending" && (
                    <div className="text-[10px] uppercase tracking-widest font-bold">
                      <span className={r.status === "approved" ? "text-[#2E7D32]" : "text-[#C62828]"}>{r.status}</span>
                      <div className="text-[#0D2B5E]/50 normal-case tracking-normal">{r.note || ""}</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#0D2B5E]/50 text-sm">No {tab} edit requests.</td></tr>}
          </tbody>
        </table>
        {controls}
      </div>
    </div>
  );
}

/* ====================== OVER-RECEIPT requests ======================== */
function OverSection() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState({});
  const [suppliers, setSuppliers] = useState({});
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    const [r, m, s] = await Promise.all([
      api.get(`/over-receipt-requests${tab !== "all" ? `?status=${tab}` : ""}`),
      api.get("/materials"),
      api.get("/suppliers"),
    ]);
    setRows(r.data || []);
    setMaterials(Object.fromEntries((m.data || []).map((x) => [x.id, x])));
    setSuppliers(Object.fromEntries((s.data || []).map((x) => [x.id, x])));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const { visibleRows, controls } = usePager(rows, 10);

  const decide = async (rid, action) => {
    if (action === "reject" && !(note[rid] || "").trim()) {
      toast.error("Rejection note is required.");
      return;
    }
    setBusy((b) => ({ ...b, [rid]: true }));
    try {
      await api.post(`/over-receipt-requests/${rid}/${action}`, { note: note[rid] || "" });
      toast.success(action === "approve" ? "Approved + line stocked-in" : "Rejected");
      setNote((n) => ({ ...n, [rid]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally {
      setBusy((b) => ({ ...b, [rid]: false }));
    }
  };

  const matLabel = (id) => {
    const m = materials[id]; return m ? `${m.code} — ${m.name}` : id?.slice(0, 8) + "…";
  };
  const supLabel = (id) => suppliers[id]?.name || "—";

  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm">
      <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center gap-3 bg-[#F5F5F5]">
        <AlertTriangle size={14} className="text-[#C62828]"/>
        <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Over-Receipt Requests (&gt;105%)</div>
        <div className="ml-auto flex gap-2">
          {["pending", "approved", "rejected", "all"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`or-tab-${t}`}
              className={`text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-sm ${tab === t ? "bg-[#1565C0] text-white" : "border border-[#EEEEEE] text-[#0D2B5E]/70"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
            <tr>
              <th className="text-left px-4 py-2">GRN</th>
              <th className="text-left px-4 py-2">Supplier</th>
              <th className="text-left px-4 py-2">Material</th>
              <th className="text-right px-4 py-2">Qty</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-right px-4 py-2">Decision</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.id} className="border-b border-[#EEEEEE]" data-testid={`or-req-${r.id}`}>
                <td className="px-4 py-2 text-xs font-mono text-[#1565C0]">{r.grn_number || r.grn_id?.slice(0, 8)}</td>
                <td className="px-4 py-2 text-xs">{supLabel(r.supplier_id)}</td>
                <td className="px-4 py-2 text-xs">{matLabel(r.material_id)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.qty}</td>
                <td className="px-4 py-2 text-xs text-[#C62828]">{r.block_reason || "—"}</td>
                <td className="px-4 py-2 text-right">
                  {r.status === "pending" && (
                    <div className="flex flex-col gap-1 items-end">
                      <input value={note[r.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                        placeholder="Note (required for reject)" data-testid={`or-note-${r.id}`}
                        className="border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm w-48"/>
                      <div className="flex gap-1">
                        <button onClick={() => decide(r.id, "approve")} disabled={busy[r.id]} data-testid={`or-approve-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold bg-[#2E7D32] text-white px-2 py-1 rounded-sm disabled:opacity-50 flex items-center gap-1"><Check size={10}/>Approve</button>
                        <button onClick={() => decide(r.id, "reject")} disabled={busy[r.id]} data-testid={`or-reject-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold bg-[#C62828] text-white px-2 py-1 rounded-sm disabled:opacity-50 flex items-center gap-1"><Ban size={10}/>Reject</button>
                      </div>
                    </div>
                  )}
                  {r.status !== "pending" && (
                    <div className="text-[10px] uppercase tracking-widest font-bold">
                      <span className={r.status === "approved" ? "text-[#2E7D32]" : "text-[#C62828]"}>{r.status}</span>
                      <div className="text-[#0D2B5E]/50 normal-case tracking-normal">{r.note || ""}</div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#0D2B5E]/50 text-sm">No {tab} over-receipt requests.</td></tr>}
          </tbody>
        </table>
        {controls}
      </div>
    </div>
  );
}

/* ============================ DELETE requests ============================ */
function DeleteSection() {
  // Re-uses the existing DeletedRecords page logic in lightweight form.
  // For now we just direct users there to keep PR small.
  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm p-6 text-center">
      <Trash2 size={24} className="mx-auto text-[#0D2B5E]/40 mb-3"/>
      <div className="text-sm text-[#0D2B5E]/70 mb-3">Delete approvals are managed on the dedicated Deleted Records page.</div>
      <a href="/deleted-records" data-testid="goto-deleted-records"
        className="inline-block text-[11px] uppercase tracking-widest font-bold text-white bg-[#1565C0] hover:bg-[#0D2B5E] px-4 py-2 rounded-sm">Open Deleted Records →</a>
    </div>
  );
}
